import { useCallback, useState } from "react";

import { useHistory } from "react-router";
import { type AttributionFilter } from "@doitintl/cmp-models";

import { useApiContext } from "../../../../api/context";
import { allocationTxt } from "../../../../assets/texts/CloudAnalytics/allocation";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../../utils";
import useSegmentTrackEvent from "../../../../utils/useSegmentTrackEvent";
import { type CloudAnalyticsHistoryState } from "../../types";
import { createAllocation, updateAllocation } from "../db";
import { trackAllocationEvent } from "../utils";
import useAllocationDndStore from "./useAllocationDnd";
import { formStoreManager, type Rule } from "./useAllocationFormState";

type RuleAction = "create" | "update" | "select";

type AllocationRule = {
  id?: string | null;
  name: string;
  action: RuleAction;
  filters: AttributionFilter[];
  formula: string;
  draft: boolean;
};

type AllocationData = {
  name: string;
  description: string;
  unallocatedCosts?: string;
  rules: AllocationRule[];
};

type UseAllocationSaveProps = {
  edit?: boolean;
  allocationId?: string;
  isDuplicate: boolean;
  isSaveAs?: boolean;
};

export const useAllocationSave = ({ edit, allocationId, isDuplicate, isSaveAs }: UseAllocationSaveProps) => {
  const [saving, setSaving] = useState(false);
  const history = useHistory<CloudAnalyticsHistoryState>();
  const { customer } = useCustomerContext();
  const api = useApiContext();
  const successSnackbar = useSuccessSnackbar(3);
  const errorSnackbar = useErrorSnackbar(3);
  const { trackEvent } = useSegmentTrackEvent();
  const { items, hasUnallocated, unallocatedCosts } = useAllocationDndStore();

  const handleClose = useCallback(() => {
    history.length > 0 ? history.goBack() : history.push(`/customers/${customer.id}/analytics/allocations`);
  }, [history, customer.id]);

  const getAction = useCallback(
    (rule: Rule & { unallocatedCosts?: string | undefined }): RuleAction => {
      if (!rule.selectedExistingRule || isDuplicate) {
        return "create";
      }

      const isRuleEqual =
        JSON.stringify({
          name: rule.selectedExistingRule.data.name,
          filters: rule.selectedExistingRule.data.filters,
          formula: rule.selectedExistingRule.data.formula,
          unallocatedCosts: rule.unallocatedCosts,
        }) ===
        JSON.stringify({
          name: rule.values.name,
          filters: rule.values.filterFields,
          formula: rule.values.formula,
          unallocatedCosts: rule.unallocatedCosts,
        });

      return isRuleEqual && rule.values.id ? "select" : "update";
    },
    [isDuplicate]
  );

  const getWarnings = useCallback(
    () => items.main.map((item) => formStoreManager.getStore(item).getState().editWarnings).filter(Boolean) || [],
    [items.main]
  );

  const handleCreate = useCallback(
    async (baseAllocationData: AllocationData) => {
      const response = await createAllocation(api, customer.id, baseAllocationData);
      trackAllocationEvent({
        trackEvent,
        eventName: "Allocation Created",
        ruleCount: baseAllocationData.rules.length,
        hasUnallocated: Boolean(baseAllocationData.unallocatedCosts),
        allocationId: response.data.id,
      });
      successSnackbar(allocationTxt.SUCCESSFULLY_CREATED);
      return true;
    },
    [api, customer.id, trackEvent, successSnackbar]
  );

  const handleUpdate = useCallback(
    async (allocationId: string, baseAllocationData: AllocationData) => {
      await updateAllocation(api, customer.id, allocationId, baseAllocationData);
      trackAllocationEvent({
        trackEvent,
        eventName: "Allocation Edited",
        ruleCount: baseAllocationData.rules.length,
        hasUnallocated: Boolean(baseAllocationData.unallocatedCosts),
        allocationId,
      });
      successSnackbar(allocationTxt.SUCCESSFULLY_UPDATED);
      return true;
    },
    [api, customer.id, trackEvent, successSnackbar]
  );

  const handleSave = useCallback(
    async (name?: string, shouldRedirect: boolean = true) => {
      setSaving(true);
      const isCreatingNew = !edit;
      const rules = items.main.map((item) => formStoreManager.getStore(item).getState());
      const allocationName = rules.length > 1 || hasUnallocated || isDuplicate ? name : rules[0].values.name;
      try {
        const baseAllocationData: AllocationData = {
          name: allocationName || "Allocation",
          description: name || rules[0].values.name,
          ...(hasUnallocated && { unallocatedCosts }),
          rules: rules.map((rule) => ({
            ...(!isDuplicate ? { id: rule.values.id, name: rule.values.name } : { name: name || rule.values.name }),
            action: getAction({ ...rule, ...(hasUnallocated && { unallocatedCosts }) }),
            filters: rule.values.filterFields,
            formula: rule.values.formula.trim(),
            draft: false,
          })),
        };

        let result = false;
        if (isCreatingNew || isSaveAs) {
          result = await handleCreate(baseAllocationData);
        } else if (allocationId) {
          result = await handleUpdate(allocationId, baseAllocationData);
        }

        if (result && shouldRedirect) {
          handleClose();
        }
        return result;
      } catch (e) {
        consoleErrorWithSentry(e);
        errorSnackbar(allocationTxt.FAILED_TO_SAVE);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      edit,
      items.main,
      hasUnallocated,
      unallocatedCosts,
      isSaveAs,
      handleClose,
      isDuplicate,
      getAction,
      handleCreate,
      handleUpdate,
      allocationId,
      errorSnackbar,
    ]
  );

  return {
    saving,
    handleSave,
    handleClose,
    getWarnings,
  };
};
